import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select, { components } from 'react-select';

import { getNestedErrorMessage } from '@/utils/utils';
import ValidationErrorMessage from '@/components/error/validation-error-component';
import useSelectStyle from '@/hooks/use-select-style';
import Styled from './select-search.style';

const CheckboxOption = ({
  getStyles,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  ...rest
}) => {
  // prop assignment
  const props = {
    ...innerProps,
  };

  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      getStyles={getStyles}
      innerProps={props}
    >
      <Styled.CheckboxContainer data-cy={`cb${children}`}>
        {children}
        <Styled.CheckBox
          type="checkbox"
          checked={isSelected}
          onChange={() => null}
        />
        <Styled.CheckMark className="checkMark" />
      </Styled.CheckboxContainer>
    </components.Option>
  );
};

CheckboxOption.defaultProps = {
  getStyles: undefined,
  isDisabled: false,
  isFocused: false,
  isSelected: false,
  children: undefined,
  innerProps: undefined,
};

CheckboxOption.propTypes = {
  getStyles: PropTypes.func,
  isDisabled: PropTypes.bool,
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  innerProps: PropTypes.shape({}),
};

/**
 * Control component definition
 * @param {object} props - The props object
 * @param {ReactNode} props.children - The children elements
 * @param {ReactNode} props.icon - The icon element to render (optional)
 * @returns {object} - Control component
 */
const Control = ({ children, icon, ...rest }) => (
  <components.Control {...rest}>
    {icon}
    {children}
  </components.Control>
);

Control.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.node,
};

Control.defaultProps = {
  icon: undefined,
};

/**
 * The Raw Select component
 * @param {object} props - props
 * @returns {JSX.Element}
 */
export const RawSelect = (props) => {
  const {
    header,
    name,
    required,
    customStyle,
    options,
    isMulti,
    allowSelectAll,
    showCheckBox,
    label,
    menuPlacement,
    placeholder,
    isClearable,
    isSearchable,
    isDisabled,
    isLoading,
    filterOption,
    customComponents,
    fontSize,
    onChange,
    cy,
    onInputChange,
    closeMenuOnSelect,
    controlShouldRenderValue,
    hideSelectedOptions,
    maxMenuHeight,
    margin,
    icon,
    noOptionsMessage,
    isGrouped,
    groupedOptions,
    defaultMenuIsOpen,
    control,
    formState,
    renderError,
  } = props;

  const { errors, isLoading: formIsLoading } = formState;

  const selectStyle = useSelectStyle();
  const allOption = useMemo(() => ({ label: 'Select All', value: '*' }), []);

  useEffect(() => {
    if (allowSelectAll && options.length > 0) {
      options.splice(0, 0, allOption);
    }
  }, [allOption, allowSelectAll, options]);

  /**
   * @returns {object} Components object
   */
  const getComponents = () => {
    const controlComponent = icon
      ? {
          /**
           * Control component
           * @param {object} props - The props object
           * @param {ReactNode} props.children - The children elements
           * @returns {object} - Control component
           */
          Control,
        }
      : {};

    const checkBox = showCheckBox
      ? {
          Option: CheckboxOption,
        }
      : {};

    return { ...controlComponent, ...checkBox, ...customComponents };
  };

  /**
   *
   * @param {*} data - data
   * @returns {void}
   */
  const formatGroupLabel = (data) => (
    <Styled.GroupContainer>
      <Styled.Span>{data?.label}</Styled.Span>
    </Styled.GroupContainer>
  );

  /**
   *
   * @param {*} value - value
   * @returns {void}
   */
  const getSelectedOptionFromGroups = (value) => {
    let returnValue;
    if (isMulti) {
      returnValue = [];
    }

    groupedOptions.forEach((groupOption) => {
      if (isMulti) {
        groupOption.options.forEach((option) => {
          if (value.includes(option.value)) {
            returnValue.push(option);
          }
        });
      } else {
        const selectedItem = groupOption.options.find(
          (option) => option.value === value,
        );

        if (selectedItem) {
          returnValue = {
            label: groupOption.label,
            options: [selectedItem],
          };
        }
      }
    });

    return returnValue;
  };

  /**
   * @returns {void}
   */
  const getOptions = () => {
    if (isLoading && formIsLoading) return [];

    if (isGrouped) return groupedOptions;

    return options;
  };

  const errorMessage = getNestedErrorMessage(name, errors);

  return (
    <Styled.Container style={customStyle} margin={margin}>
      <Styled.SelectHeader error={errorMessage} fontSize={fontSize}>
        {header}
      </Styled.SelectHeader>
      <Controller
        control={control}
        name={name}
        rules={required}
        render={({ field: { onChange: onValueChange, value } }) => {
          const handleChange = (selected, event) => {
            if (onChange) {
              onChange(selected);
            }
            if (isMulti) {
              if (allowSelectAll) {
                if (
                  event.option.value === '*' &&
                  event.action === 'select-option'
                ) {
                  return onValueChange(options.map((option) => option.value));
                }
                if (
                  event.option.value === '*' &&
                  event.action === 'deselect-option'
                ) {
                  return onValueChange([]);
                }
              }
              return onValueChange(selected.map((sel) => sel.value));
            }

            return onValueChange(selected ? selected.value : selected);
          };

          const getSelected = () => {
            if (!value) {
              return null;
            }
            if (isMulti && value) {
              if (allowSelectAll) {
                const arr = [allOption, ...options];
                if (value.length > 0 && value.length === options.length) {
                  const selectedAllOption = value.find(
                    (option) => option === '*',
                  );
                  if (!selectedAllOption) {
                    value.splice(0, 0, '*');
                  }
                }
                return arr.filter((option) =>
                  value.find((val) => option.value === val),
                );
              }

              return isGrouped
                ? getSelectedOptionFromGroups(value)
                : options.filter((option) =>
                    value.find((val) => option.value === val),
                  );
            }

            if (isGrouped) {
              return getSelectedOptionFromGroups(value);
            }

            let selected;

            const getNestedSelect = (opts) => {
              opts.forEach((option) => {
                if (option.value === value) {
                  selected = option;
                }
                if (option.options) {
                  getNestedSelect(option.options);
                }
              });
            };

            getNestedSelect(options);

            return selected;
          };

          return (
            <Select
              // React select cannot add data-cy so you have to work with Id attribute
              id={cy}
              {...props}
              selectProps={{ defaultMenuIsOpen, errorMessage }}
              label={label}
              name={name}
              placeholder={placeholder}
              styles={selectStyle}
              value={getSelected()}
              isClearable={isClearable}
              isMulti={isMulti}
              allowSelectAll={allowSelectAll}
              isSearchable={isSearchable}
              isDisabled={isDisabled}
              closeMenuOnSelect={closeMenuOnSelect}
              controlShouldRenderValue={controlShouldRenderValue}
              hideSelectedOptions={hideSelectedOptions}
              filterOption={filterOption}
              components={getComponents()}
              isLoading={isLoading && formIsLoading}
              onChange={handleChange}
              menuPlacement={menuPlacement}
              onInputChange={onInputChange}
              maxMenuHeight={maxMenuHeight}
              menuPortalTarget={document.getElementById('select-option')}
              menuShouldScrollIntoView={false}
              noOptionsMessage={() => noOptionsMessage ?? 'No options'}
              formatGroupLabel={isGrouped ? formatGroupLabel : undefined}
              options={getOptions()}
            />
          );
        }}
      />
      {renderError && <ValidationErrorMessage message={errorMessage} />}
    </Styled.Container>
  );
};

const SelectPropTypes = {
  fontSize: PropTypes.string,
  customComponents: PropTypes.shape({}),
  styles: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  validationLabel: PropTypes.string,
  menuPlacement: PropTypes.string,
  required: PropTypes.shape({}),
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  className: PropTypes.string,
  isClearable: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  error: PropTypes.shape({}),
  placeholder: PropTypes.string,
  customStyle: PropTypes.shape({}),
  isMulti: PropTypes.bool,
  showCheckBox: PropTypes.bool,
  header: PropTypes.string,
  filterOption: PropTypes.func,
  cy: PropTypes.string,
  controlShouldRenderValue: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  allowSelectAll: PropTypes.bool,
  maxMenuHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  margin: PropTypes.string,
  icon: PropTypes.node,
  noOptionsMessage: PropTypes.string,
  isGrouped: PropTypes.bool,
  groupedOptions: PropTypes.arrayOf(PropTypes.shape({})),
  defaultMenuIsOpen: PropTypes.bool,
  control: PropTypes.shape({}).isRequired,
  formState: PropTypes.shape({
    errors: PropTypes.shape({}),
    isLoading: PropTypes.bool,
  }).isRequired,
  renderError: PropTypes.bool,
};
const SelectDefaultProps = {
  fontSize: '',
  customComponents: {},
  styles: {},
  isLoading: false,
  isDisabled: false,
  isSearchable: true,
  closeMenuOnSelect: true,
  header: '',
  validationLabel: undefined,
  menuPlacement: 'bottom',
  required: {},
  options: [],
  label: '',
  className: '',
  isClearable: true,
  onChange: undefined,
  onInputChange: undefined,
  error: null,
  placeholder: 'Please Select',
  customStyle: {},
  isMulti: false,
  filterOption: undefined,
  cy: undefined,
  margin: undefined,
  controlShouldRenderValue: true,
  showCheckBox: false,
  hideSelectedOptions: false,
  allowSelectAll: false,
  maxMenuHeight: '300px',
  icon: undefined,
  noOptionsMessage: undefined,
  isGrouped: false,
  groupedOptions: [],
  defaultMenuIsOpen: false,
  renderError: true,
};

RawSelect.defaultProps = SelectDefaultProps;

RawSelect.propTypes = SelectPropTypes;

// Normal select
/**
 * The Select Search component
 * @param {object} props - props
 * @returns {JSXElement} - SelectList
 */
const SelectList = (props) => {
  const { control, formState } = useFormContext();

  return <RawSelect {...props} control={control} formState={formState} />;
};
SelectList.defaultProps = SelectDefaultProps;

SelectList.propTypes = SelectPropTypes;

export default SelectList;
